import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsApi } from '../../utils/reportsApi';
import { api } from '../../utils/api';
import './ReportCommon.css';

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}
function fmt(n) {
  const v = Number(n);
  if (!v) return '';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// default date range = current fiscal year (Sep 1 to today)
function defaultDateFrom() {
  const now = new Date();
  const year = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  return `${year}-09-01`;
}
function defaultDateTo() {
  return new Date().toISOString().slice(0, 10);
}

const YtdReportPage = () => {
  const navigate = useNavigate();

  const [dateFrom,      setDateFrom]      = useState(defaultDateFrom());
  const [dateTo,        setDateTo]        = useState(defaultDateTo());
  const [accountCode,   setAccountCode]   = useState('');
  const [entityId,      setEntityId]      = useState('');
  const [postedOnly,    setPostedOnly]    = useState(true);
  const [view,          setView]          = useState('totals');
  const [includeOpening, setIncludeOpening] = useState(true);
  const [excludeJV,     setExcludeJV]    = useState(false);

  const [entities,  setEntities]  = useState([]);
  const [result,    setResult]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    api.get('/entities').then((d) => setEntities(d || [])).catch(() => {});
  }, []);

  const run = async () => {
    if (!dateFrom || !dateTo) { setError('Date range is required.'); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      const data = await reportsApi.getYtd({
        dateFrom, dateTo,
        accountCode: accountCode || undefined,
        entityId: entityId || undefined,
        postedOnly, view, includeOpening, excludeJV,
      });
      setResult(data);
    } catch (e) {
      setError(e.message || 'Failed to run report');
    } finally {
      setLoading(false);
    }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      await reportsApi.exportYtd(
        { dateFrom, dateTo, accountCode: accountCode || undefined, entityId: entityId || undefined, postedOnly, view, includeOpening, excludeJV },
        `ytd-${dateFrom}-to-${dateTo}.xlsx`
      );
    } catch (e) { setError(e.message || 'Export failed'); }
    finally { setExporting(false); }
  };

  const lines  = result?.lines  ?? [];
  const totals = result?.totals ?? {};
  const isDetail = result?.view === 'detail';

  return (
    <div className="rpt-page">
      <div className="rpt-header">
        <button className="rpt-back" onClick={() => navigate('/reports')}><i className="fas fa-arrow-left"></i> Reports</button>
        <h1 className="rpt-title">Year-to-Date (YTD)</h1>
      </div>

      <div className="rpt-filters">
        <label className="rpt-label">
          From <span className="rpt-req">*</span>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rpt-input" />
        </label>
        <label className="rpt-label">
          To <span className="rpt-req">*</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rpt-input" />
        </label>
        <label className="rpt-label">
          Account code prefix
          <input type="text" value={accountCode} onChange={(e) => setAccountCode(e.target.value)}
            placeholder="e.g. 500, 131-884" className="rpt-input" style={{ minWidth: 140 }} />
        </label>
        <label className="rpt-label">
          Entity
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)} className="rpt-select">
            <option value="">All entities</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </label>
        <label className="rpt-label">
          View
          <select value={view} onChange={(e) => setView(e.target.value)} className="rpt-select">
            <option value="totals">Totals (per account)</option>
            <option value="detail">Detail (ledger lines)</option>
          </select>
        </label>
        <label className="rpt-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={postedOnly} onChange={(e) => setPostedOnly(e.target.checked)} />
          Posted only
        </label>
        <label className="rpt-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={includeOpening} onChange={(e) => setIncludeOpening(e.target.checked)} disabled={view === 'detail'} />
          With opening balance
        </label>
        <label className="rpt-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={excludeJV} onChange={(e) => setExcludeJV(e.target.checked)} />
          Exclude JVs
        </label>
        <div className="rpt-filter-actions">
          <button className="rpt-btn rpt-btn--primary" onClick={run} disabled={loading}>
            {loading ? <><i className="fas fa-spinner fa-spin"></i> Running…</> : <><i className="fas fa-play"></i> Run</>}
          </button>
          {result && (
            <button className="rpt-btn rpt-btn--excel" onClick={exportXlsx} disabled={exporting}>
              {exporting ? <><i className="fas fa-spinner fa-spin"></i> Exporting…</> : <><i className="fas fa-file-excel"></i> Export Excel</>}
            </button>
          )}
        </div>
      </div>

      {error && <div className="rpt-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

      {result && (
        <div className="rpt-result">
          <div className="rpt-meta">
            {dateFrom} to {dateTo}
            {result.entity && <> &nbsp;·&nbsp; <strong>{result.entity.name}</strong></>}
            &nbsp;·&nbsp; {isDetail ? `${lines.length} lines` : `${lines.length} accounts`}
            {result.total === 10000 && <span style={{ color: 'var(--warning-600)' }}> (capped at 10,000 — narrow your date range)</span>}
          </div>
          <div className="rpt-table-wrap">
            {isDetail ? (
              <table className="rpt-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Type</th><th>#</th><th>Doc #</th>
                    <th className="col-name">Description</th><th className="col-name">Account</th>
                    <th className="col-amt">Dr (EGP)</th><th className="col-amt">Cr (EGP)</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr><td colSpan="8" className="rpt-empty">No transactions found.</td></tr>
                  )}
                  {lines.map((l, i) => (
                    <tr key={i}>
                      <td>{fmtDate(l.voucher?.date)}</td>
                      <td><span className={`type-chip type-chip--${l.voucher?.type?.toLowerCase()}`}>{l.voucher?.type}</span></td>
                      <td>{l.voucher?.voucherNumber}</td>
                      <td>{l.voucher?.docNumber || '—'}</td>
                      <td className="col-name">{l.voucher?.description || '—'}</td>
                      <td className="col-name">{l.account?.code} {l.account?.name}</td>
                      <td className="col-amt">{fmt(l.debitLocal)}</td>
                      <td className="col-amt">{fmt(l.creditLocal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="rpt-table">
                <thead>
                  <tr>
                    <th className="col-code">Code</th>
                    <th className="col-name">Name</th>
                    {result.includeOpening && <><th className="col-amt">Opening Dr</th><th className="col-amt">Opening Cr</th></>}
                    <th className="col-amt">Movement Dr</th>
                    <th className="col-amt">Movement Cr</th>
                    <th className="col-amt">Closing Dr</th>
                    <th className="col-amt">Closing Cr</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr><td colSpan="8" className="rpt-empty">No accounts with activity.</td></tr>
                  )}
                  {lines.map((r) => (
                    <tr key={r.accountId}>
                      <td className="col-code">{r.code}</td>
                      <td className="col-name">{r.name}</td>
                      {result.includeOpening && <><td className="col-amt">{fmt(r.openingDebit)}</td><td className="col-amt">{fmt(r.openingCredit)}</td></>}
                      <td className="col-amt">{fmt(r.movementDebit)}</td>
                      <td className="col-amt">{fmt(r.movementCredit)}</td>
                      <td className="col-amt">{fmt(r.closingDebit)}</td>
                      <td className="col-amt">{fmt(r.closingCredit)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="row-totals">
                    <td colSpan="2">TOTALS</td>
                    {result.includeOpening && <><td className="col-amt">{fmt(totals.openingDebit)}</td><td className="col-amt">{fmt(totals.openingCredit)}</td></>}
                    <td className="col-amt">{fmt(totals.movementDebit)}</td>
                    <td className="col-amt">{fmt(totals.movementCredit)}</td>
                    <td className="col-amt">{fmt(totals.closingDebit)}</td>
                    <td className="col-amt">{fmt(totals.closingCredit)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      )}

      <style>{`
        .type-chip { display:inline-block; padding:1px 8px; border-radius:999px; font-size:0.75rem; font-weight:600; }
        .type-chip--jv { background:#dbeafe; color:#1d4ed8; }
        .type-chip--rv { background:#dcfce7; color:#166534; }
        .type-chip--pv { background:#fef3c7; color:#92400e; }
      `}</style>
    </div>
  );
};

export default YtdReportPage;
